import { useState, useEffect } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getAllProperties, getIncomingInvites } from '@/lib/api/api'
import { resolveEntitlement, resolvePlanTier } from '@/lib/utils/subscriptionEntitlement'

interface BillingStatus {
  hasActiveSubscription: boolean
  hasActiveTrial: boolean
  usedTrial: boolean
  subscriptionExists: boolean // True if user has ever activated a subscription (Stripe or local trial)
  hasPendingInvites: boolean  // True if user has unaccepted workspace invites
  planName?: string
  trialEndsAt?: string
  isLoading: boolean
  error?: Error
}

/**
 * Hook to check user's billing/subscription status.
 * Returns whether user has active subscription or trial.
 *
 * Fails open: a network/server error leaves isLoading=false with no
 * access fields set to false — callers must check isLoading before
 * blocking the user. The paywall never fires on an API error.
 */
export function useBilling(): BillingStatus {
  const { token } = useAuth()

  const [status, setStatus] = useState<BillingStatus>({
    hasActiveSubscription: false,
    hasActiveTrial: false,
    usedTrial: true,
    subscriptionExists: false,
    hasPendingInvites: false,
    isLoading: true,
  })

  useEffect(() => {
    if (!token) {
      setStatus({
        hasActiveSubscription: false,
        hasActiveTrial: false,
        usedTrial: true,
        subscriptionExists: false,
        hasPendingInvites: false,
        isLoading: false,
      })
      return
    }

    let isMounted = true

    const fetchBillingStatus = async () => {
      try {
        const [data, invitesRaw] = await Promise.all([
          getAllProperties(token),
          getIncomingInvites(token).catch(() => []),
        ])

        if (!isMounted) return

        const pendingInvites: unknown[] = Array.isArray(invitesRaw)
          ? invitesRaw
          : (invitesRaw as { invites?: unknown[] })?.invites || []
        const hasPendingInvites = pendingInvites.length > 0

        const subscription = data.subscription || {}
        const currentPlan = data.currentPlan || subscription.currentPlan
        const planSource = subscription.planSource

        const subscriptionExists = data.subscriptionExists ?? false

        const planTier = resolvePlanTier(currentPlan)
        const isLocalTrial =
          subscription.localTrialActive === true || planSource === 'local_trial'
        const hasPaidStripePlan =
          planTier !== 'free' && planSource === 'stripe_subscription'

        const hasActiveTrial = !!(
          isLocalTrial ||
          (subscription.isTrial && !hasPaidStripePlan)
        )

        const hasMembershipAccess = (data.properties || []).some(
          (property: { isOwner?: boolean; subscription?: { status?: string; isFreePlan?: boolean; localTrialActive?: boolean; planSource?: string } }) =>
            !property.isOwner && propertyHasActiveSubscription(property)
        )

        // `/property/all` carries no Stripe subscription status, so entitlement
        // here rests on the plan name plus trial flags. SubscriptionContext
        // resolves the same shape with the real status from `/billing/status`
        // and is what revokes paid features on cancel — this hook only feeds
        // the hard paywall, which deliberately never re-locks a past customer.
        const { hasActiveSubscription } = resolveEntitlement({
          planName: currentPlan,
          planSource,
          stripeStatus: null,
          hasActiveTrial,
          isTrialFlagged: !!subscription.isTrial || !!subscription.localTrialActive,
          hasMembershipAccess,
        })

        setStatus({
          hasActiveSubscription: hasActiveSubscription || hasMembershipAccess,
          hasActiveTrial,
          usedTrial: data.usedTrial ?? subscription.usedTrial ?? true,
          subscriptionExists: subscriptionExists || hasMembershipAccess || hasPendingInvites,
          hasPendingInvites,
          planName: currentPlan,
          trialEndsAt: subscription.trialEnd || subscription.localTrialEndsAt,
          isLoading: false,
        })
      } catch (error) {
        if (!isMounted) return
        // Fail open: don't block the user when the billing endpoint is down.
        // isLoading=false so callers know the fetch completed, but all access
        // fields stay at their safe defaults (the paywall checks subscriptionExists).
        setStatus({
          hasActiveSubscription: false,
          hasActiveTrial: false,
          usedTrial: false,
          subscriptionExists: true, // treat as having access so paywall doesn't fire
          hasPendingInvites: false,
          isLoading: false,
          error: error as Error,
        })
      }
    }

    fetchBillingStatus()

    return () => {
      isMounted = false
    }
  }, [token])

  return status
}

/**
 * Helper function to check if user has any active access
 */
function hasActiveAccess(status: BillingStatus): boolean {
  return status.hasActiveSubscription || status.hasActiveTrial
}

/**
 * Helper function to check if a property has an active subscription
 * Used for shared team access - check property owner's subscription status
 * NOTE: Local trials do NOT count - only Stripe subscriptions count
 */
export function propertyHasActiveSubscription(property?: {
  subscription?: {
    status?: string
    isFreePlan?: boolean
    localTrialActive?: boolean
    planSource?: string
  }
}): boolean {
  if (!property?.subscription) return false

  const sub = property.subscription

  const isLocalTrial = sub.localTrialActive === true || sub.planSource === 'local_trial'

  if (isLocalTrial) {
    return false
  }

  return (
    sub.status === 'NORMAL' &&
    sub.isFreePlan === false
  )
}
